import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';

function scopeLabel(s) {
  if (s === 'platform') return 'Platform';
  if (s === 'plan') return 'Plan';
  if (s === 'tenant_override') return 'Tenant override';
  return s;
}

function statusColor(st) {
  if (st === 'active') return 'success';
  if (st === 'draft') return 'warning';
  return 'default';
}

export default function PlatformUsagePoliciesListPage() {
  const navigate = useNavigate();
  const { user } = usePlatformAuth();
  const isSuper = user?.role === 'super_admin';

  const [qInput, setQInput] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [scopeInput, setScopeInput] = useState('');
  const [appliedScope, setAppliedScope] = useState('');
  const [statusInput, setStatusInput] = useState('');
  const [appliedStatus, setAppliedStatus] = useState('');
  const [sort, setSort] = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      page: String(page + 1),
      pageSize: String(pageSize),
      sort,
      order
    });
    if (appliedQ) params.set('q', appliedQ);
    if (appliedScope) params.set('policyScope', appliedScope);
    if (appliedStatus) params.set('status', appliedStatus);
    try {
      const res = await platformApiFetch(`/api/platform/usage-policies?${params}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load policies');
      setData(json);
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, appliedQ, appliedScope, appliedStatus, sort, order]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFilters = () => {
    setPage(0);
    setAppliedQ(qInput.trim());
    setAppliedScope(scopeInput);
    setAppliedStatus(statusInput);
  };

  const policies = data?.policies || [];
  const total = data?.pagination?.total ?? 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">API usage policies</Typography>
        {isSuper && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/platform/usage-policies/new')}>
            New policy
          </Button>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Platform defaults and per-plan quotas. Tenant overrides are listed here for oversight; tenants may edit their
        own overrides when permitted.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
          <TextField
            size="small"
            label="Search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
            sx={{ minWidth: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Scope</InputLabel>
            <Select label="Scope" value={scopeInput} onChange={(e) => setScopeInput(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="platform">Platform</MenuItem>
              <MenuItem value="plan">Plan</MenuItem>
              <MenuItem value="tenant_override">Tenant override</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={statusInput} onChange={(e) => setStatusInput(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Sort</InputLabel>
            <Select label="Sort" value={sort} onChange={(e) => setSort(e.target.value)}>
              <MenuItem value="created_at">Created</MenuItem>
              <MenuItem value="policy_name">Name</MenuItem>
              <MenuItem value="status">Status</MenuItem>
              <MenuItem value="policy_scope">Scope</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Order</InputLabel>
            <Select label="Order" value={order} onChange={(e) => setOrder(e.target.value)}>
              <MenuItem value="desc">Desc</MenuItem>
              <MenuItem value="asc">Asc</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={applyFilters}>
            Apply
          </Button>
        </Box>
      </Paper>

      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Scope</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell>Enforcement</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Default</TableCell>
                  <TableCell align="right"> </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {policies.map((p) => (
                  <TableRow key={p.id} hover>
                    <TableCell>{p.policy_name}</TableCell>
                    <TableCell>{scopeLabel(p.policy_scope)}</TableCell>
                    <TableCell>
                      {p.policy_scope === 'plan' && (p.plan_name || `#${p.plan_id}`)}
                      {p.policy_scope === 'tenant_override' &&
                        `${p.tenant_name || '—'} (${p.tenant_code || p.tenant_id})`}
                      {p.policy_scope === 'platform' && '—'}
                    </TableCell>
                    <TableCell>{p.enforcement_mode}</TableCell>
                    <TableCell>
                      <Chip size="small" label={p.status} color={statusColor(p.status)} />
                    </TableCell>
                    <TableCell>{p.is_platform_default ? 'Yes' : '—'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="View">
                        <IconButton size="small" onClick={() => navigate(`/platform/usage-policies/${p.id}`)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {isSuper && (
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => navigate(`/platform/usage-policies/${p.id}/edit`)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!policies.length && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography color="text.secondary">No policies found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_e, p) => setPage(p)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
